import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GlassHeroContainer } from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const FeesPage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: '96px', sm: '136px' },
        }}
      >
        {/* Heading Section */}
        <HeadingSection>
          <Heading sx={{ mb: { xs: '24px', tablet: '48px' } }}>
            Platform fees
          </Heading>
          <Typography
            sx={(theme) => ({
              fontFamily: {
                xs: 'montserrat, sans-serif',
                sm: 'roboto, sans-serif',
              },
              fontSize: { xs: '12px', sm: '16px' },
              lineHeight: { xs: '166%', sm: '150%' },
              letterSpacing: { xs: '.4px', sm: '.15px' },
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
              textAlign: 'center',
            })}
          >
            Last updated: 23 Feb 2026
          </Typography>
        </HeadingSection>

        {/* Content Section */}
        <ContentSection>
          <ContentWrapper>
            <BodyText>
              At WoM, we believe in keeping things simple. We built this
              platform to connect people, not to build a maze of hidden fees and
              complicated subscriptions. What you see is exactly what you get.
            </BodyText>

            <BodyText>
              <strong>For Customers: Always Free</strong> <br />
              Finding trusted local help should never cost you extra.
              <ul>
                <li>
                  0% Platform Fees: Using WoM to search, chat, and book
                  professionals is completely free.
                </li>
                <li>
                  No Hidden Markups: The price you agree on with the
                  professional is the price you pay. Clients do not pay
                  additional platform fees.
                </li>
              </ul>
            </BodyText>
            <BodyText>
              <strong>For Professionals: A Simple 10%</strong> <br />
              We believe we should only succeed when you do.
              <ul>
                <li>
                  Flat Rate: We charge a straightforward 10% commission on the
                  jobs you complete through the platform. The 10% platform fee
                  is calculated on the total service price paid by the client
                  and is automatically deducted at the time of payment before
                  payout to the professional.
                </li>
                <li>
                  Payment Processing: Payments on Womapp are securely processed
                  via Stripe or PayPal. In addition to the 10% platform fee,
                  payment providers may apply standard processing fees depending
                  on the payment method, account type, issuing country, or
                  currency conversion. These fees are determined by the
                  respective provider and may vary.
                </li>
                <li>
                  Transparency: Payment processing fees are set and collected by
                  Stripe or PayPal and are not controlled or retained by Womapp.
                </li>
                <li>
                  No Surprises: There are no monthly subscription fees, no
                  "pay-per-connect" charges, and no hidden admin costs. You get
                  direct access to clients, and we keep the platform running
                  smoothly.
                </li>
              </ul>
            </BodyText>

            <BodyText>
              <strong>The platform fee helps cover:</strong> <br />
              <ul>
                <li>Identity and profile verification tools.</li>
                <li>Job discovery and matching.</li>
                <li>In-app messaging and booking tools.</li>
                <li>Trust & safety and customer support.</li>
                <li>Platform operations and improvements.</li>
              </ul>
            </BodyText>
          </ContentWrapper>
        </ContentSection>

        {/* Get Started CTA */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: '24px', tablet: '48px' },
            mb: { xs: '24px', tablet: '48px' },
          }}
        >
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Heading Section
const HeadingSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '956px',
  marginBottom: '24px',
  [theme.breakpoints.down('tablet')]: {
    marginBottom: '16px',
  },
}));

// About Us Heading
const Heading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
    fontWeight: 700,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}));

// Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Content Wrapper
const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',

  '& strong': {
    fontWeight: 700,
  },
  '& ul': {
    margin: 0,
    paddingLeft: '20px',
    listStyle: 'none',
  },
  '& li:before': {
    content: '"•"',
    position: 'relative',
    left: '-12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

export default FeesPage;
